import { pluralize } from '@racemap/utilities/formattings/formatting';
import {
  isLineString,
  isPoint,
  isPointOnLineString,
  isShadowTrack,
} from '@racemap/utilities/functions/validation';
import { type FC, type ReactNode, useEffect } from 'react';
import { useControl } from 'react-map-gl/mapbox';
import {
  IconEdit,
  IconExpand,
  IconFail,
  IconLocationPin,
  IconOk,
  IconSave,
  IconTrash,
} from '../../Icons';
import type { DrawMode } from '../Editor';
import { EDITING_MODES, events, modes } from '../Editor/consts';
import { getModeLabelText } from '../Editor/utils';
import { ButtonGroupControl, type ControlButton } from './ButtonGroupControl';
import {
  getFeatureLabel,
  getMoveTitle,
  getRemoveTitle,
  hasStartOrFinishSplitInSelection,
  type SelectedFeatures,
} from './utils';

interface Props {
  mode: DrawMode | null;
  onChange?: (newMode: DrawMode) => void;
  onZoomToSelection?: () => void;
  onCancel?: () => void;
  selectedFeatures: SelectedFeatures;
  allFeatures?: SelectedFeatures;
}

interface ButtonProps {
  key: string;
  icon?: ReactNode;
  label: ReactNode;
  title?: string;
  onClick?: () => void;
  disabled?: boolean;
  isLabel?: boolean;
}

class DrawModeControlClass extends ButtonGroupControl {
  onChange?: (newStyle: DrawMode) => void;
  mode: DrawMode | null = null;
  selectedFeatures: SelectedFeatures = [];
  allFeatures: SelectedFeatures = [];
  onZoomToSelection?: () => void;
  onCancel?: () => void;

  constructor(
    mode: DrawMode | null,
    selectedFeatures: SelectedFeatures,
    allFeatures: SelectedFeatures,
    onChange?: (newMode: DrawMode) => void,
    onZoomToSelection?: () => void,
    onCancel?: () => void,
  ) {
    super();
    this.mode = mode;
    this.onChange = onChange;
    this.selectedFeatures = selectedFeatures;
    this.allFeatures = allFeatures;
    this.onZoomToSelection = onZoomToSelection;
    this.onCancel = onCancel;
  }

  getButtonProps(): Array<ButtonProps> {
    const props: Array<ButtonProps> = [];
    const mode: DrawMode = this.mode ?? modes.SIMPLE_SELECT;
    const selectedFeatures = this.selectedFeatures;

    if (mode === modes.SIMPLE_SELECT) {
      if (selectedFeatures.length === 0) {
        props.push({
          key: 'select',
          label: <strong>Select Elements</strong>,
          title: 'Select elements on the map',
          isLabel: true,
        });
      } else {
        props.push({
          key: 'move',
          label: <strong>Move {pluralize(selectedFeatures.length, 'Element')}</strong>,
          title: getMoveTitle(selectedFeatures),
          isLabel: true,
        });
      }

      if (selectedFeatures.length === 1 && !isPoint(selectedFeatures[0])) {
        props.push({
          key: 'modify',
          icon: <IconEdit fixedWidth />,
          label: 'Modify points',
          title: `Modify ${getFeatureLabel(selectedFeatures[0])}`,
          onClick: () =>
            this._map?.fire(events.MODE_CHANGE, {
              mode: modes.DIRECT_SELECT,
              featureId: selectedFeatures[0].id,
            }),
        });
      }

      if (selectedFeatures.length === 1 && isLineString(selectedFeatures[0])) {
        props.push({
          key: 'addSplit',
          icon: <IconLocationPin fixedWidth />,
          label: 'Add Split',
          title: isShadowTrack(selectedFeatures[0])
            ? `Add split to ${getFeatureLabel(selectedFeatures[0])}`
            : 'Splits can only be added to shadow tracks',
          disabled: !isShadowTrack(selectedFeatures[0]),
          onClick: () =>
            this._map?.fire(events.MODE_CHANGE, {
              mode: modes.POINT_ON_LINE_STRING,
              featureId: selectedFeatures[0].id,
            }),
        });
      }

      if (selectedFeatures.length === 1 && isPointOnLineString(selectedFeatures[0])) {
        props.push({
          key: 'modifySplit',
          icon: <IconEdit fixedWidth />,
          label: 'Modify',
          title: `Modify ${getFeatureLabel(selectedFeatures[0])}`,
          onClick: () =>
            this._map?.fire(events.MODE_CHANGE, {
              mode: modes.POINT_ON_LINE_STRING,
              featureIds: [selectedFeatures[0].id, selectedFeatures[0].properties?.lineStringId],
            }),
        });
      }

      if (selectedFeatures.length > 0) {
        props.push(
          {
            key: 'delete',
            icon: <IconTrash fixedWidth />,
            label: 'Remove',
            title: getRemoveTitle(selectedFeatures, this.allFeatures),
            disabled: hasStartOrFinishSplitInSelection(selectedFeatures, this.allFeatures),
            onClick: () =>
              this._map?.fire(events.DELETE, {
                features: selectedFeatures,
              }),
          },
          {
            key: 'zoom',
            icon: <IconExpand fixedWidth />,
            label: 'Zoom',
            onClick: () => this.onZoomToSelection?.(),
          },
          {
            key: 'done',
            icon: <IconSave fontSize="1.0rem" />,
            label: 'Save',
            title: 'Finish editing by saving current state and switch back to select mode',
            onClick: () =>
              this._map?.fire(events.MODE_CHANGE, {
                mode: modes.SIMPLE_SELECT,
                featureIds: [],
              }),
          },
          {
            key: 'cancel',
            icon: <IconFail fontSize="1.2rem" />,
            label: 'Cancel',
            title: 'Cancel current action and switch to select mode',
            onClick: () => {
              this.onCancel?.();
              this._map?.fire(events.MODE_CHANGE, {
                mode: modes.SIMPLE_SELECT,
                featureIds: [],
              });
            },
          },
        );
      }
    }

    if (EDITING_MODES.includes(mode)) {
      const modifyFeature = selectedFeatures[0];
      const mainLabelText = getModeLabelText(mode, modifyFeature?.geometry.type);

      props.push({
        key: mainLabelText,
        label: <strong>{mainLabelText}</strong>,
        isLabel: true,
      });

      if (mode === modes.DIRECT_SELECT) {
        props.push({
          key: 'removeVertex',
          icon: <IconTrash fixedWidth />,
          label: 'Remove',
          title: 'Remove selected vertex (Delete)',
          onClick: () => this._map?.fire(events.TRASH),
        });
      }

      props.push({
        key: 'done',
        icon: <IconOk fontSize="1.0rem" />,
        label: 'Done',
        title: 'Finish editing and switch to select mode',
        onClick: () =>
          this._map?.fire(events.MODE_CHANGE, {
            mode: modes.SIMPLE_SELECT,
            featureIds: [],
          }),
      });

      props.push({
        key: 'cancel',
        icon: <IconFail fontSize="1.2rem" />,
        label: 'Cancel',
        title: 'Cancel current action and switch to select mode',
        onClick: () => {
          this.onCancel?.();
          this._map?.fire(events.MODE_CHANGE, {
            mode: modes.SIMPLE_SELECT,
            featureIds: [],
          });
        },
      });
    }

    return props;
  }

  getButtons(): Array<ControlButton> {
    return this.getButtonProps().map(({ key, icon, label, title, onClick, disabled, isLabel }) => ({
      title: title ?? (typeof label === 'string' ? label : undefined),
      onClick,
      disabled,
      isLabel,
      content: (
        <div key={key} style={{ display: 'flex', alignItems: 'center' }}>
          {icon != null && (
            <div style={{ display: 'flex', alignItems: 'center', marginRight: 2 }}>{icon}</div>
          )}
          <div style={{ fontWeight: 400 }}>{label}</div>
        </div>
      ),
    }));
  }
}

export const DrawModeControl: FC<Props> = ({
  mode,
  onChange,
  selectedFeatures,
  allFeatures = [],
  onZoomToSelection,
  onCancel,
}) => {
  const ctrl = useControl<DrawModeControlClass>(
    () =>
      new DrawModeControlClass(
        mode,
        selectedFeatures,
        allFeatures,
        onChange,
        onZoomToSelection,
        onCancel,
      ),
    {
      position: 'top-left',
    },
  );

  useEffect(() => {
    ctrl.mode = mode;
    ctrl.selectedFeatures = selectedFeatures;
    ctrl.allFeatures = allFeatures;
    ctrl.onChange = onChange;
    ctrl.onZoomToSelection = onZoomToSelection;
    ctrl.onCancel = onCancel;
    ctrl.render();
  }, [mode, onChange, ctrl, onZoomToSelection, onCancel, selectedFeatures, allFeatures]);

  return null;
};
